import * as React from 'react';
import { createContext, ReactElement, createRef } from 'react';
import { Toast, ToastMessage } from 'primereact/toast';
import { confirmDialog, ConfirmDialog, ConfirmDialogProps } from 'primereact/confirmdialog';
import { IntlShape, useIntl } from 'react-intl';
import { ApiResponse, VoidApiResponse } from '../../api/core';

export interface ToastContextType {
  notify: (options: ToastMessage | ToastMessage[]) => void;
  alert: (options: ConfirmDialogProps) => void;
  confirm: (options: ConfirmDialogProps) => void;
  error: (
    error: ApiResponse<any> | VoidApiResponse | unknown,
    key?: string,
    params?: { [key: string]: string }
  ) => void;
}

type ApiErrorBody = {
  error?: string;
};

const isResponse = (value: unknown): value is Response => value instanceof Response;

const getErrorResponse = (error: unknown): Response | undefined => {
  if (isResponse(error)) {
    return error;
  }

  if (!error || typeof error !== 'object') {
    return undefined;
  }

  const candidate = error as {
    raw?: unknown;
    response?: unknown;
  };

  if (isResponse(candidate.response)) {
    return candidate.response;
  }

  if (isResponse(candidate.raw)) {
    return candidate.raw;
  }

  return undefined;
};

const parseErrorBody = async (response: Response): Promise<ApiErrorBody | undefined> => {
  try {
    const text = await response.clone().text();

    if (!text) {
      return undefined;
    }

    return JSON.parse(text) as ApiErrorBody;
  } catch (err) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('Failed to parse API error response body.', err);
    }

    return undefined;
  }
};

export const ToastContext: React.Context<ToastContextType> = createContext<ToastContextType>(undefined);

export const ToastProvider = ({ children }: { children: any }): ReactElement => {
  const intl: IntlShape = useIntl();
  const notifierRef: React.RefObject<Toast> = createRef<Toast>();
  const confirmRef: React.RefObject<ConfirmDialog> = createRef<ConfirmDialog>();

  const notify = (message: ToastMessage | ToastMessage[]): void => {
    if (!notifierRef.current) return;
    notifierRef.current.show(message);
  };

  const alert = (dialog: ConfirmDialogProps): void => {
    if (!dialog) return;
    confirm({
      rejectClassName: 'hidden',
      acceptLabel: intl.formatMessage({ id: 'button.ok' }),
      icon: 'pi pi-exclamation-triangle',
      ...dialog,
    });
  };

  const confirm = (dialog: ConfirmDialogProps): void => {
    if (!dialog) return;
    confirmDialog({
      className: 'max-w-30rem',
      ...dialog,
    });
  };

  const error = (
    _error: ApiResponse<any> | VoidApiResponse | unknown,
    key?: string,
    params?: { [key: string]: string }
  )=> {
    void errorAsync (_error, key, params);
  };

  const errorAsync = async (
    _error: ApiResponse<any> | VoidApiResponse | unknown,
    key?: string,
    params?: { [key: string]: string }
  ): Promise<void> => {
    const response = getErrorResponse(_error);

    if (response?.status === 422 || response?.status === 424) {
      const errorBody = await parseErrorBody(response);

      if (errorBody?.error) {
        alert({
          header: intl.formatMessage({ id: key ? `${key}.title` : 'message.error.title' }),
          message: intl.formatMessage({ id: `errormsg.code.${errorBody.error}` }, params),
        });
      } else {
        alert({
          header: intl.formatMessage({ id: key ? `${key}.title` : 'message.error.title' }),
          message: intl.formatMessage({ id: `${JSON.stringify(errorBody)}` }, params),
        });
      }
      return;
    }

    if (key) {
      alert({
        header: intl.formatMessage({ id: `${key}.title` }),
        message: intl.formatMessage({ id: `${key}.message` }, params),
      });
      return;
    }

    alert({
      header: intl.formatMessage({ id: 'message.error.title' }),
      message: intl.formatMessage({ id: 'message.error.message' }, params),
    });
  };

  return (
    <ToastContext.Provider value={{ notify, alert, confirm, error }}>
      <Toast key={'notifier'} ref={notifierRef} position={'top-right'}></Toast>
      <ConfirmDialog key={'confirm'} ref={confirmRef} />
      <div key={'content'}>{children}</div>
    </ToastContext.Provider>
  );
};
